import { PaginatedMeasurementRequestParams, WindowSize } from '@restDataAccess';
import { MeasurementRangeQuery, InventoryProvider } from '@scaleaq/sitecontrol-lib';
import { DateTime } from 'luxon';

import { ChartAggregationResolution } from '@/components/chart/types.ts';
import { fromApiDateTime, toApiCalendarDay, toApiDate } from '@/utils/apiDateTime.ts';

import { type WelfareProvider, WelfareProviders } from './measurement.types';

const DEFAULT_PAGE_SIZE = 100000;

export const paginatedRequestToQueryString = (query: PaginatedMeasurementRequestParams) => {
  if (!query.pageSize) query.pageSize = DEFAULT_PAGE_SIZE;
  if (!query.page) query.page = 1;
  return Object.entries(query)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .flatMap(([key, value]) => toQueryParams({ key, value }))
    .join('&');
};

export const requestToQueryString = (query: object) =>
  Object.entries(query)
    .filter(([, value]) => !!value)
    .flatMap(([key, value]) => toQueryParams({ key, value }))
    .join('&');

/** Arrays become one repeated `key=` pair per item (`types=A&types=B`), as the API binds them. */
const toQueryParams = ({ key, value }: { key: string; value: unknown }) =>
  (Array.isArray(value) ? value : [value]).map(item => `${key}=${handleURIParameter(item)}`);

const handleURIParameter = (value: unknown) => {
  if (value instanceof Date && !isNaN(value.getTime()))
    return encodeURIComponent(value.toISOString());
  return encodeURIComponent(value as string | number | boolean);
};

/** Stable query key for range requests; avoids millisecond drift breaking React Query cache. */
export const getMeasurementRangeSubSensorCacheKey = (
  request: MeasurementRangeQuery
): (string | number)[] => [
  request.inventoryId,
  request.type ?? '',
  request.resolution,
  request.windowSize ?? '',
  request.end
    ? request.end.length === 10
      ? request.end
      : (fromApiDateTime(request.end).startOf('hour').toISO() ?? '')
    : '',
];

export const isWelfareProvider = (provider: InventoryProvider): provider is WelfareProvider =>
  (WelfareProviders as readonly InventoryProvider[]).includes(provider);

/**
 * Interval covering the requested number of weeks, ending at the last complete week.
 * Shared by the welfare score and OrbitOne heatmap requests so they ask for the same window and
 * hit the same query cache. `offset` shifts the whole window back in time.
 *
 * The API treats `end` as exclusive, so `end` is the midnight after the last day to include (as in
 * the lice count window). Days are API (Oslo) days, so the window ends where the API's last full day
 * does in any browser zone. Midnight bounds keep the window, and so the query key, stable within
 * the day.
 */
const getWeeklyRangeInterval = ({
  weeks,
  offset = { days: 0 },
}: {
  weeks: number;
  offset?: { days: number };
}) => {
  const today = toApiCalendarDay(DateTime.now());
  // Start of the base day: the exclusive end of the day before, as we do not expect data for the
  // current day
  const base = today.minus(offset);

  // By default, interval covers the single day before base, to ensure a range
  let end = base;
  let start = base.minus({ days: 1 });

  if (weeks === 1) {
    // Last seven full days, excluding today
    start = base.minus({ weeks: 1 });
  } else if (weeks > 1) {
    const dropCurrentWeek = today.weekday === 1 && offset.days === 0;
    const startWeekOffset = weeks - (dropCurrentWeek ? 0 : 1);

    // Multi-week ranges cover complete Monday-Sunday weeks, ending at the following Monday.
    start = base.minus({ weeks: startWeekOffset }).startOf('week');
    end = start.plus({ weeks });
  }

  return { start, end };
};

export type ApiDateRange = { start: string; end: string };

export type WelfareScoresWindow = ApiDateRange & {
  resolution: ChartAggregationResolution;
  windowSize: WindowSize;
};

/**
 * The welfare scores request window for a number of weeks back: `getWeeklyRangeInterval` as API
 * dates, with the aggregation that fits its length. Multi-week windows are aggregated per week;
 * anything shorter per day over a three-day window. Widgets that think in weeks go through this, so
 * requests for the same window build identical query keys and share the cache.
 */
export const getWelfareScoresWindow = ({
  weeks,
  offset,
}: {
  weeks: number;
  offset?: { days: number };
}): WelfareScoresWindow => {
  const { start, end } = getWeeklyRangeInterval({ weeks, offset });
  const isMultiWeek = weeks > 1;

  return {
    start: toApiDate(start),
    end: toApiDate(end),
    resolution: isMultiWeek ? ChartAggregationResolution.WEEKLY : ChartAggregationResolution.DAILY,
    windowSize: isMultiWeek ? WindowSize.SevenDays : WindowSize.ThreeDays,
  };
};

/**
 * `getWeeklyRangeInterval` cut into one API date range per week, oldest first, so a widget that
 * needs many weeks can request them one at a time instead of as a single heavy range. Ranges of
 * one week or less are not Monday–Sunday aligned, so they come back as the single interval.
 */
export const getWeeklyApiRanges = ({ weeks }: { weeks: number }): ApiDateRange[] => {
  const { start, end } = getWeeklyRangeInterval({ weeks });

  if (weeks <= 1) return [{ start: toApiDate(start), end: toApiDate(end) }];

  return Array.from({ length: weeks }, (_, index) => {
    const weekStart = start.plus({ weeks: index });

    // Exclusive end: the Monday after this week
    return { start: toApiDate(weekStart), end: toApiDate(weekStart.plus({ weeks: 1 })) };
  });
};
